<template>
  <div>
    <va-modal
      ref="paymentModal"
      class="evollu-form"
      :value="showPaymentModal"
      :okText="$t('licenses.payments.save')"
      :okClass="'btn btn-primary btn-micro'"
      :okToClose="false"
      @ok="submit"
      :cancelText="$t('licenses.payments.back')"
      :cancelClass="'btn-cancelar btn btn-primary btn-micro'"
      @cancel="close"
      :showDelete="isUpdate"
      :dangerText="$t('modals.buttons.delete')"
      @remove="prepareForDestroy"
    >

      <div slot="header" class="modal-title">
        <div class="row">
          <div class="col">
            <b>{{ $t('licenses.payments.create_payment') }}</b>
          </div>
        </div>
      </div>

      <!-- Section Title -->
      <div class="col-12">
        <label class="form-label section-label">{{$t('licenses.payments.payment_info')}}</label>
      </div>

      <div class="col-12 my-2 d-flex justify-content-center" v-if="form.errors.has('license')">
        <small class="help text-danger" v-if="form.errors.has('license')">
          {{ $t(form.errors.get('license')) }}
        </small>
      </div>

      <div class="row">
        <div class="form-group col-12 col-md-6">
          <div class="input-group">
            <va-input
              :placeholder="$t('licenses.payments.document_reference')"
              v-model="form.data.document_reference"
              maxLength="75"
              id="document-reference"
              :error="form.errors.has('document_reference')"
              :error-messages="[$t(`${form.errors.get('document_reference')}`)]"
              @input="form.errors.clear('document_reference')"
              required/>
          </div>
        </div>

        <div class="form-group with-icon-right col-12 col-md-6">
          <div class="input-group">
            <va-input
              :placeholder="$t('licenses.payments.amount')"
              v-model="form.data.amount"
              maxLength="10"
              type="number"
              step="0.01"
              id="document-reference"
              :error="form.errors.has('amount')"
              :error-messages="[$t(`${form.errors.get('amount')}`)]"
              @input="form.errors.clear('amount')"
              required />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="form-group col-12 col-md-6">
          <div class="input-group datepicker">
            <DatePicker
              id="date-picker-range"
              class="w-100"
              :placeholder="$t('licenses.payments.date')"
              v-model="form.data.date"
              @input="form.errors.clear('date')" />
              <small v-show="form.errors.has('date')"  class="help text-danger">
                {{ $t(form.errors.get('date')) }}
              </small>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="form-group">
            <div class="input-group">
              <va-input
                v-model="form.data.obs"
                :placeholder="$t('agenda.inputs.observations')"
                type="textarea"
                :minRows="3"
                :maxRows="8" />
            <small v-show="form.errors.has('obs')" class="help text-danger">
                {{ $t(form.errors.get('obs')) }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </va-modal>

    <ConfirmationModal
      v-if="isDestroyingPayment"
      :title="$t('licenses.payments.delete_payment')"
      ::body="$t('licenses.payments.delete_modal_body', {document_reference: payment.document_reference, amount: payment.amount})"
      @modal-confirmed="destroyPayment"
      @modal-canceled="cancelDestroyPayment" />

  </div>
</template>
<script>
import { LicensePaymentsService } from '@/api/LeadsService'
import Errors from '@/utils/StandardErrors.js'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import DatePicker from '@/components/common/DatePicker'

export default {
  props: {
    license: {
      type: Object,
      required: true
    },
    payment: {
      type: Object,
      required: false
    }
  },
  components: {
    DatePicker,
    ConfirmationModal
  },
  mounted: function () {
    this.showPaymentModal = true
    if (this.payment) {
      this.prepareForUpdate()
    }
  },
  data: function () {
    return {
      isUpdate: false,
      showPaymentModal: false,
      isDestroyingPayment: false,
      form: {
        data: {
          document_reference: null,
          date: null,
          amount: null,
          obs: null
        },
        errors: new Errors()
      }
    }
  },
  methods: {
    submit: function () {
      let data = this.form.data
      let request = this.isUpdate ? LicensePaymentsService.update(this.license.id, this.payment.id, data) : LicensePaymentsService.store(this.license.id, data)

      request
        .then(response => {
          this.showToast(this.isUpdate ? this.$t('licenses.payments.payment_updated') : this.$t('licenses.payments.payment_created'), this.$store.state.successToastOptions)
          this.$emit(this.isUpdate ? 'updated' : 'created', response.data.data)
          this.showPaymentModal = false
        })
        .catch(errors => {
          if (errors.response.status === 422) {
            this.form.errors.record(errors.response.data.errors, 'licenses.payments.errors.')
            this.showToast(this.$t('licenses.payments.form_contains_errors'), this.$store.state.errorToastOptions)
          } else if (errors.response.status === 403) {
            this.showToast(this.$t('licenses.payments.missing_permissions'), this.$store.state.errorToastOptions)
          } else {
            this.showToast(this.$t('licenses.payments.something_went_wrong'), this.$store.state.errorToastOptions)
          }
        })
    },
    close: function () {
      if (!this.isDestroyingPayment) {
        setTimeout(() => {
          this.$emit('modalClosed')
        }, 450)
      }
    },
    prepareForUpdate: function () {
      this.form.data = JSON.parse(JSON.stringify(this.payment))
      this.isUpdate = true
    },
    prepareForDestroy: function () {
      this.isDestroyingPayment = true
      this.showPaymentModal = false
    },
    cancelDestroyPayment: function () {
      this.isDestroyingPayment = false
      this.showPaymentModal = true
    },
    destroyPayment: function () {
      this.isDestroyingPayment = false
      LicensePaymentsService.destroy(this.license.id, this.payment.id)
        .then(response => {
          this.isDestroyingLicense = false
          this.showPaymentModal = false
          this.showToast(this.$t('licenses.payments.payment_deleted'), this.$store.state.successToastOptions)
          this.$nextTick(() => {
            this.$emit('destroyed')
          })
        })
        .catch(errors => this.showToast(this.$t('licenses.something_went_wrong'), this.$store.state.errorToastOptions))
    }
  }
}
</script>
